import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ConfigurationService } from './configuration.service';
import { User } from './labpartner.service.model';

export interface TestUserInfo extends User {
  roles: string[];
}

export interface TestUser {
  oid: string;
  name: string;
  email: string;
  roles: string[];
}

@Injectable({
  providedIn: 'root',
})
export class TestLoginService {
  private currentUserSubject = new BehaviorSubject<TestUserInfo | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private readonly testUsers: { [key: string]: TestUser } = {
    admin: {
      oid: 'd60d888a-5bcb-4d3d-b081-401c0dbfdedd',
      name: 'Automation Admin',
      email: 'admin@quidelortho.com',
      roles: [
        'LabPartner.Administrators',
        'LabPartner.Writers',
        'LabPartner.Readers',
        'ResultsBundler.ClinicalTrials',
        'LabPartner.Viewers',
      ],
    },
    writer: {
      oid: 'a60d888a-5bcb-4d3d-b081-401c0dbfdedd',
      name: 'Automation Writer',
      email: 'writer@quidelortho.com',
      roles: ['LabPartner.Writers', 'LabPartner.Readers', 'LabPartner.Viewers'],
    },
    reader: {
      oid: 'b60d888a-5bcb-4d3d-b081-401c0dbfdedd',
      name: 'Automation Reader',
      email: 'reader@quidelortho.com',
      roles: ['LabPartner.Readers', 'LabPartner.Viewers'],
    },
  };

  constructor(
    private http: HttpClient,
    private configService: ConfigurationService,
  ) {}

  /**
   * Initialize test login based on localStorage value
   */
  async initializeTestLogin(): Promise<void> {
    const testUserType = localStorage.getItem('cypress-test-user');

    if (testUserType && this.testUsers[testUserType]) {
      console.log(`[TestLoginService] Initializing test login for: ${testUserType}`);
      await this.loginWithTestToken(this.testUsers[testUserType]);
    }
  }

  /**
   * Login using TestToken endpoint
   */
  async loginWithTestToken(testUser: TestUser): Promise<void> {
    try {
      console.log('[TestLoginService] Sending request to TestToken endpoint with user:', testUser);

      const apiEndpoint = this.configService.getConfig().apiServiceEndpoint;
      const testTokenUrl = `${apiEndpoint}TestToken`;
      const response = await this.http
        .post<string>(testTokenUrl, {
          oid: testUser.oid,
          name: testUser.name,
          email: testUser.email,
          roles: testUser.roles,
        })
        .toPromise();

      if (response) {
        console.log('[TestLoginService] Received token from TestToken endpoint');
        // Store the token in localStorage (similar to MSAL)
        this.storeTestToken(response, testUser);

        // Set current user
        const user: TestUserInfo = {
          userId: parseInt(testUser.oid.replace(/\D/g, '').slice(0, 8)) || 1,
          accountId: testUser.oid,
          displayName: testUser.name,
          email: testUser.email,
          roles: testUser.roles,
        };

        this.currentUserSubject.next(user);
        console.log(`[TestLoginService] Successfully logged in as: ${testUser.name}`);

        // Note: UserAccountService will be updated by AppComponent after page reload
      }
    } catch (error) {
      console.error('[TestLoginService] Failed to login with test token:', error);
    }
  }

  /**
   * Store test token in localStorage (MSAL format)
   */
  private storeTestToken(token: string, testUser: TestUser): void {
    console.log('[TestLoginService] Storing test token in localStorage');

    // Clear previous MSAL tokens from localStorage
    this.clearPreviousTokens();

    const tenantId = '6e63ffc0-c2fc-4cc4-b6c4-666b2ce89d92';
    const clientId = '6b79bf94-8a9e-4a78-ad23-de2e7dc13ee3';
    const target = 'api://QuidelOrtho.LabPartner.Dev/ExperimentData.All';
    const environment = 'login.windows.net';

    const localAccountId = testUser.oid;
    const homeAccountId = `${localAccountId}.${tenantId}`;
    const now = Math.floor(Date.now() / 1000);

    // MSAL v3 AccountInfo structure
    const accountInfo = {
      homeAccountId: homeAccountId,
      localAccountId: localAccountId,
      username: testUser.email,
      environment: environment,
      tenantId: tenantId,
      name: testUser.name,
      idTokenClaims: {
        aud: clientId,
        exp: (now + 100000).toString(),
        name: testUser.name,
        oid: localAccountId,
        preferred_username: testUser.email,
        roles: testUser.roles,
        tid: tenantId,
      },
    };

    // MSAL v3 AccessTokenEntity structure
    const accessTokenEntity = {
      homeAccountId: homeAccountId,
      environment: environment,
      credentialType: 'AccessToken',
      clientId: clientId,
      secret: token,
      target: target,
      cachedAt: now.toString(),
      expiresOn: (now + 100000).toString(),
      extendedExpiresOn: (now + 100000).toString(),
      tokenType: 'Bearer',
      realm: tenantId,
    };

    // Store in localStorage
    const accountKey = `${homeAccountId}-${environment}-${tenantId}`;
    const accessTokenKey = `${homeAccountId}-${environment}-accesstoken-${clientId}-${tenantId}-${target}--`;

    console.log('[TestLoginService] Storing account info with key:', accountKey);
    console.log('[TestLoginService] Storing access token with key:', accessTokenKey);

    localStorage.setItem(accountKey, JSON.stringify(accountInfo));
    localStorage.setItem(accessTokenKey, JSON.stringify(accessTokenEntity));

    // Store additional MSAL v3 cache entries
    const clientInfoKey = `${homeAccountId}-${environment}-clientInfo`;
    const clientInfo = {
      uid: localAccountId,
      utid: tenantId,
    };
    localStorage.setItem(clientInfoKey, JSON.stringify(clientInfo));

    console.log('[TestLoginService] Token stored successfully in localStorage');
  }

  /**
   * Clear previous MSAL tokens from localStorage
   */
  private clearPreviousTokens(): void {
    console.log('[TestLoginService] Clearing previous MSAL tokens from localStorage');

    const keys = Object.keys(localStorage);
    const msalKeys = keys.filter(
      key =>
        key.includes('6b79bf94-8a9e-4a78-ad23-de2e7dc13ee3') ||
        key.includes('6e63ffc0-c2fc-4cc4-b6c4-666b2ce89d92') ||
        key.includes('accesstoken') ||
        key.includes('clientInfo'),
    );

    msalKeys.forEach(key => {
      console.log('[TestLoginService] Removing key:', key);
      localStorage.removeItem(key);
    });

    console.log('[TestLoginService] Cleared', msalKeys.length, 'MSAL keys from localStorage');
  }

  /**
   * Get current user
   */
  getCurrentUser(): TestUserInfo | null {
    return this.currentUserSubject.value;
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user ? user.roles.includes(role) : false;
  }

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    return this.hasRole('ADMIN');
  }

  /**
   * Logout (clear localStorage)
   */
  logout(): void {
    localStorage.clear();
    this.currentUserSubject.next(null);
  }
}
